import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Link } from "react-router-dom";
import Navbar from "./components/Navbar";
import Home from "./components/Home";
import Services from "./components/Services";
import Portfolio from "./components/Portfolio";
import About from "./components/About";
import Contact from "./components/Contact";
import FAQ from "./components/Faq";
import Privacy from "./components/Privacy";
import NotFound from "./components/NotFound";
import LuxeManeStudio from "./demos/luxe-mane/LuxeManeStudio";
import TherapyApp from "./demos/therapy-booking/TherapyApp";
import BrewedShop from "./demos/BrewedShop/BrewedShop";
import "./index.css";
import ScrollToTop from "./components/ScrollToTop";

/* =================================================================
   SEO: per-page title + description + canonical + index rules.
   No extra library. Edit the copy below anytime.
   Any path NOT listed here is automatically set to noindex
   (covers the demos, the /portfolio redirect, and the 404).
   ================================================================= */
const SITE_URL = "https://layerandco.com";

const SEO = {
  "/": {
    title: "Layer & Co. | Custom Web Design for NY, NJ & CT Small Businesses",
    description:
      "Web design studio building custom, flat-priced websites for small businesses across New York, New Jersey, and Connecticut. You own the code and domain.",
  },
  "/services": {
    title: "Web Design Services & Pricing | Layer & Co.",
    description:
      "Four flat-priced website tiers for service and product businesses: Starter, Business, Practice, and Commerce. Clear scope and timelines. Serving the NY tri-state area.",
  },
  "/live-previews": {
    title: "Live Website Previews | Layer & Co. Portfolio",
    description:
      "Explore interactive demo sites built by Layer & Co., from single-page sites to full e-commerce. See each service tier working before you commit.",
  },
  "/about": {
    title: "About | Layer & Co. Freelance Web Designer (NY, NJ, CT)",
    description:
      "A one-person web design studio serving small businesses across New York, New Jersey, and Connecticut. Direct work, flat pricing, and code you own.",
  },
  "/contact": {
    title: "Contact | Start a Website Project with Layer & Co.",
    description:
      "Start a custom website for your small business. Book a free intro call or send a project inquiry. Serving New York, New Jersey, and Connecticut.",
  },
  "/faq": {
    title: "FAQ | Web Design Pricing, Timeline & Process | Layer & Co.",
    description:
      "Answers on pricing, timelines, payment terms, what you own, and how Layer & Co.'s web design process works for small businesses in the tri-state area.",
  },
  "/privacy": {
    title: "Privacy Policy | Layer & Co.",
    description:
      "How Layer & Co. collects, uses, and protects information submitted through layerandco.com.",
  },
};

function upsertMeta(attr, key, content) {
  if (content == null) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function SeoManager() {
  const { pathname } = useLocation();

  React.useEffect(() => {
    const known = SEO[pathname];
    const meta = known || { title: "Layer & Co.", description: "", noindex: true };
    const url = SITE_URL + pathname;

    document.title = meta.title;
    upsertMeta("name", "description", meta.description);
    upsertMeta("property", "og:title", meta.title);
    upsertMeta("property", "og:description", meta.description);
    upsertMeta("property", "og:url", url);
    upsertMeta("name", "robots", meta.noindex ? "noindex, follow" : "index, follow");
    upsertLink("canonical", url);
  }, [pathname]);

  return null;
}

function AppLayout() {
  const { pathname } = useLocation();

  const isDemoRoute =
    pathname.startsWith("/portfolio/starter") ||
    pathname.startsWith("/portfolio/practice") ||
    pathname.startsWith("/portfolio/commerce");

  return (
    <div className="app">
      <SeoManager />
      <ScrollToTop />
      {!isDemoRoute && <Navbar />}

      <main className="page-content">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<Services />} />
          <Route path="/live-previews" element={<Portfolio />} />
          <Route path="/portfolio" element={<Navigate to="/live-previews" replace />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/privacy" element={<Privacy />} />

          <Route path="/portfolio/starter" element={<LuxeManeStudio />} />
          <Route path="/portfolio/practice/*" element={<TherapyApp />} />
          <Route path="/portfolio/commerce/*" element={<BrewedShop />} />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>

      {!isDemoRoute && (
        <footer className="site-footer">
          <p>© {new Date().getFullYear()} Layer &amp; Co. · Web Studio</p>
          <Link to="/privacy" className="footer-privacy-link">Privacy policy</Link>
        </footer>
      )}
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <AppLayout />
    </Router>
  );
}